import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import './index.css';
import { RootState } from '@/store';
import Button from '@/components/common/Button';
import {
	bookingInsightType,
	bookingInsightTypes,
} from '@/pages/Report/constant';
import { formatCapitalWords } from '@/utils';

interface BookingInsightFilterProps {
	handleUpdate: (params: any) => void;
}

const getOneMonthBeforeToday = (timestamp: number) => {
	const oneMonthAgo = new Date(timestamp);
	oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
	return oneMonthAgo.getTime();
};

const BookingInsightFilter: React.FC<BookingInsightFilterProps> = ({
	handleUpdate,
}) => {
	const venues = useSelector((state: RootState) => state.userInfo.state.venues);
	const [filters] = useSearchParams();

	const defaultStartDateValue = filters.get('startDate')
		? parseInt(filters.get('startDate'), 10)
		: getOneMonthBeforeToday(Date.now());
	const defaultEndDateValue = filters.get('endDate')
		? parseInt(filters.get('endDate'), 10)
		: Date.now();
	const defaultVenueId = filters.get('venueId') ?? venues[0].id;
	const defaultCurrBookingType =
		filters.get('insightType') ?? bookingInsightType.BOOKING_TIMES;

	const [venueId, setVenueId] = useState(defaultVenueId);
	const [currBookingType, setCurrBookingType] = useState(
		defaultCurrBookingType
	);
	const [startDate, setStartDate] = useState(defaultStartDateValue);
	const [endDate, setEndDate] = useState(defaultEndDateValue);

	const handleVenueIdChange = (event) => {
		setVenueId(event.target.value);
	};

	const handleCurrBookingTypeChange = (event) => {
		setCurrBookingType(event.target.value);
	};

	const handleStartDateChange = (value: Date) => {
		setStartDate(value.getTime());
	};

	const handleEndDateChange = (value: Date) => {
		setEndDate(value.getTime());
	};

	const handleUpdateData = () => {
		const params = {
			venueId,
			insightType: currBookingType,
			startDate: startDate.toString(),
			endDate: endDate.toString(),
		} as UpdateParamObject;
		handleUpdate(params);
	};

	return (
		<div className="card p-7 j-col gap-7">
			{venues.length > 0 && (
				<>
					<div className="text-jlg font-bold">Query</div>
					<div className="j-col gap-6">
						<div className="j-col gap-2">
							<div className="filter-input-title">Start Date</div>
							<DatePicker
								selected={startDate}
								onChange={handleStartDateChange}
								className="input-picker-style"
							/>
						</div>
						<div className="j-col gap-2">
							<div className="filter-input-title">End Date</div>
							<DatePicker
								selected={endDate}
								onChange={handleEndDateChange}
								className="input-picker-style"
							/>
						</div>
						<div className="j-col gap-2">
							<div className="filter-input-title">Insight Type</div>
							<div className="w-full">
								<select
									value={currBookingType}
									onChange={handleCurrBookingTypeChange}
									className="input-picker-style"
								>
									{bookingInsightTypes.map((v) => (
										<option key={v} value={v}>
											{formatCapitalWords(v)}
										</option>
									))}
								</select>
							</div>
						</div>
						{venues.length > 0 &&
							currBookingType !== bookingInsightType.VENUE && (
								<div className="j-col gap-2">
									<div className="filter-input-title">Venue</div>
									<div className="w-full">
										<select
											value={venueId || venues[0].id}
											onChange={handleVenueIdChange}
											className="input-picker-style"
										>
											{venues.map((v) => (
												<option key={v.id} value={v.id}>
													{v.name}
												</option>
											))}
										</select>
									</div>
								</div>
							)}
					</div>
					<Button
						type="submit"
						variant="primary"
						className="whitespace-nowrap flex gap-2 px-12 py-2 justify-center items-center text-jsm font-medium"
						form="bookingForm"
						onClick={() => {
							handleUpdateData();
						}}
					>
						Update
					</Button>
				</>
			)}
		</div>
	);
};
export default BookingInsightFilter;
